/**
 * Hook dữ liệu của nhóm màn vận hành: Tổng quan, Nhật ký truy vết, Thử nghiệm. View chỉ gọi hook ở đây.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { khoTroLy } from '../../../lib/repositories/tro-ly-ai.repository';
import type { CauHinhDem, LocNhatKyHoiThoai } from '../types';
import { KHOA, thuLai } from './useChung';

const NHIP_TAI_LAI = 30_000;

const KHOA_VAN_HANH = {
  luotChay: [...KHOA.nhatKy, 'luot-chay'] as const,
  hoiThoai: (loc: LocNhatKyHoiThoai) => [...KHOA.nhatKy, 'hoi-thoai', loc] as const,
  chiTiet: (cvId: string | null) => [...KHOA.nhatKy, 'hoi-thoai', 'chi-tiet', cvId] as const,
  dem: ['tro-ly-ai', 'cau-hinh-dem'] as const,
  nguCanh: (sourceId: string, cauHoi: string) => ['tro-ly-ai', 'ngu-canh', sourceId, cauHoi] as const,
};

/**
 * Số liệu sử dụng. Một khoá theo `ngay`, dùng chung cho MỌI khối trên cùng màn: Tổng quan có bảy khối đọc cùng một
 * payload, mỗi khối tự gọi là bảy lời gọi — với bảy con số có thể lệch nhau nếu ai đó hỏi giữa chừng.
 */
export function useSuDung(ngay: number) {
  return useQuery({
    queryKey: KHOA.suDung(ngay),
    queryFn: () => khoTroLy.suDung(ngay),
    staleTime: 60_000,
    retry: thuLai,
  });
}

/** Chu kỳ đồng bộ và câu SQL đã qua bộ canh cổng — tự làm mới mỗi 30 giây. */
export function useLuotChay() {
  return useQuery({
    queryKey: KHOA_VAN_HANH.luotChay,
    queryFn: () => khoTroLy.luotChay(undefined, 200),
    staleTime: NHIP_TAI_LAI,
    refetchInterval: NHIP_TAI_LAI,
    retry: thuLai,
  });
}

/** Lịch sử trò chuyện XUYÊN vai — chỉ quản trị (403 với vai thường, không thử lại). */
export function useHoiThoaiNhatKy(loc: LocNhatKyHoiThoai) {
  return useQuery({
    queryKey: KHOA_VAN_HANH.hoiThoai(loc),
    queryFn: () => khoTroLy.nhatKyHoiThoai(loc),
    staleTime: NHIP_TAI_LAI,
    // Giữ danh sách cũ khi đang gõ ô tìm — xoá trắng rồi vẽ lại mỗi phím là danh sách nhấp nháy dưới tay người gõ.
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useChiTietHoiThoai(cvId: string | null) {
  return useQuery({
    queryKey: KHOA_VAN_HANH.chiTiet(cvId),
    queryFn: () => khoTroLy.nhatKyMoHoiThoai(cvId ?? ''),
    enabled: cvId !== null,
    staleTime: NHIP_TAI_LAI,
    retry: thuLai,
  });
}

export function useCauHinhDem() {
  return useQuery({
    queryKey: KHOA_VAN_HANH.dem,
    queryFn: khoTroLy.cauHinhDem,
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Gửi PHẦN đổi; giá trị máy chủ ĐÃ KẸP ghi thẳng vào cache để form vẽ lại từ đó. */
export function useDatCauHinhDem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (thay: Partial<CauHinhDem>) => khoTroLy.datCauHinhDem(thay),
    onSuccess: (moi) => qc.setQueryData(KHOA_VAN_HANH.dem, moi),
  });
}

/** Gói ngữ cảnh mô hình nhận được — nguyên văn chuỗi đi vào lời nhắc. */
export function useGoiNguCanh(sourceId: string, cauHoi: string) {
  return useQuery({
    queryKey: KHOA_VAN_HANH.nguCanh(sourceId, cauHoi),
    queryFn: () => khoTroLy.goiNguCanh(sourceId, cauHoi || undefined),
    enabled: !!sourceId,
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Chạy thẳng qua bộ canh cổng, KHÔNG có mô hình nào tham gia. */
export function useChayCauLenh() {
  return useMutation({
    mutationFn: (du: { sourceId: string; sql: string; question?: string }) => khoTroLy.chayCauLenh(du),
  });
}

/** Lưu câu vừa chạy được thành câu mẫu, rồi làm mới gói ngữ cảnh (câu mẫu nằm trong gói). */
export function useThemCauMau() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { sourceId: string; question: string; sql: string; generatedSql?: string | null }) =>
      khoTroLy.themCauMau(v),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ['tro-ly-ai', 'ngu-canh', v.sourceId] }),
  });
}
